import { ConversationParticipantEntity } from '@provision/conversation-types';
import dayjs from 'dayjs';
import { formatMessageGroupDate } from '..';
import { ConversationChannelTypeExtended, } from './conversation-types';
import { BOT_NAMES } from './placeholders';
export function formatMessageData(message, { conversationId, partnerId, baseUrl, participants, } = {}) {
    return {
        id: message.conversationMessageId,
        messageDispatches: message.messageDispatches.map((i) => ({
            ...i,
            participant: participants?.[i.participation?.channel?.conversationParticipantId],
        })),
        partnerId: message.partnerId,
        conversationId: message.conversationId,
        message: message.body,
        emailFileKey: message.incomingConversationEmail?.fileKey,
        createdAt: message.createdAt,
        editedAt: message.editedAt,
        senderEntity: message.senderChannel.conversationParticipant
            .participantEntity,
        isPrivate: message.isPrivate,
        senderId: message.senderChannel.conversationParticipant.participantIdentity,
        attachments: message.attachments.map((attachment) => ({
            ...attachment,
            url: `${baseUrl ?? ''}${message.partnerId || partnerId || ''}/${message.conversationId || conversationId || ''}/${attachment.fileKey}`,
        })),
    };
}
export function groupMessagesByParticipantIdentity({ conversationId, partnerId, baseUrl, messages, participants, formatMessage, }) {
    const groups = [];
    let currentGroup = {};
    let currentGroupDate = '';
    for (let i = 0; i < messages.length; i++) {
        const currentMessage = messages[i];
        const previousMessage = messages[i - 1];
        const data = formatMessage(messages[i], {
            conversationId,
            partnerId,
            baseUrl,
            participants,
        });
        const MIN_GROUPING_TIME_DIFFERENCE_HOUR = 1;
        const overAnHourPassedSinceLastMessage = dayjs(currentGroupDate).diff(dayjs(data.createdAt), 'hour') >=
            MIN_GROUPING_TIME_DIFFERENCE_HOUR;
        const overADayPassedSinceLastMessage = !dayjs(currentGroupDate).isSame(data.createdAt, 'day');
        const isAnotherParticipantsMessageGroup = currentMessage.senderChannel.conversationParticipant.participantIdentity !==
            previousMessage?.senderChannel.conversationParticipant.participantIdentity;
        const isAnotherPrivate = currentMessage.isPrivate !== previousMessage?.isPrivate;
        if (!previousMessage ||
            isAnotherPrivate ||
            overAnHourPassedSinceLastMessage ||
            isAnotherParticipantsMessageGroup) {
            const { conversationParticipantId, participantIdentity, participantEntity } = currentMessage.senderChannel.conversationParticipant ?? {};
            /*
              Since we're parsing messages in reverse order, from newest to oldest,
              only the oldest group of messages withing a single day should have a date label,
              so remove a label from a newer groups if they have less than a day difference.
            */
            if (!overADayPassedSinceLastMessage && currentGroup.label) {
                currentGroup.label = '';
            }
            currentGroupDate = data.createdAt || '';
            const date = dayjs(currentGroupDate);
            const isToday = date.isSame(dayjs(), 'day');
            const channelType = isInternalNote(currentMessage)
                ? ConversationChannelTypeExtended.InternalNote
                : currentMessage.senderChannel.channelType?.id ||
                    ConversationChannelTypeExtended.Chat;
            let sender = null;
            switch (participantEntity) {
                case ConversationParticipantEntity.PlatformUser.toString():
                case ConversationParticipantEntity.Client.toString():
                    sender = participants[conversationParticipantId];
                    break;
                case ConversationParticipantEntity.Rsm.toString():
                    sender = {
                        firstName: participants[conversationParticipantId]?.name,
                        lastName: '',
                    };
                    break;
                case ConversationParticipantEntity.Bot.toString(): {
                    if (!sender && conversationParticipantId) {
                        sender = {
                            firstName: BOT_NAMES[participantIdentity],
                            lastName: '',
                        };
                    }
                    break;
                }
                case ConversationParticipantEntity.Unknown.toString(): {
                    if (!sender && participantIdentity) {
                        sender = {
                            firstName: participantIdentity,
                            lastName: '',
                        };
                    }
                    break;
                }
            }
            currentGroup = {
                id: data.id,
                createdAt: currentGroupDate,
                messages: [data],
                sender,
                senderEntity: participantEntity,
                channelType,
                label: isToday ? 'TODAY' : formatMessageGroupDate(currentGroupDate),
            };
            groups.push(currentGroup);
        }
        else {
            currentGroup.messages = [data, ...currentGroup.messages];
            currentGroup.createdAt = data.createdAt;
        }
    }
    return groups;
}
export function isInternalNote(message) {
    return (message.isPrivate &&
        message.senderChannel.channelType?.id === ConversationChannelTypeExtended.Chat);
}
